import { useCallback, useEffect, useRef, useState } from "react";
import { historyBeats, type HistoryBeat } from "../../data/history";
import { useMediaQuery, usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { HistoryParagraph } from "../CartridgeHover/CartridgeHover";
import { RifleSilhouette } from "./RifleSilhouette";
import styles from "./HistoryScroll.module.css";

function navOffset() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--nav-h");
  const probe = document.createElement("div");
  probe.style.height = raw.trim() || "4.25rem";
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  document.body.appendChild(probe);
  const px = probe.offsetHeight;
  probe.remove();
  return px || 68;
}

function documentTop(el: HTMLElement) {
  return el.getBoundingClientRect().top + window.scrollY;
}

function trackRange(track: HTMLElement) {
  const nav = navOffset();
  const top = documentTop(track);
  const start = top - nav;
  const end = top + track.offsetHeight - window.innerHeight;
  return { start, end, span: Math.max(1, end - start) };
}

function beatFromScroll(track: HTMLElement, n: number) {
  const { start, span } = trackRange(track);
  const t = (window.scrollY - start) / span;
  const clamped = Math.min(1, Math.max(0, t));
  return Math.min(n - 1, Math.floor(clamped * n + 1e-6));
}

function BeatCopy({ item }: { item: HistoryBeat }) {
  return (
    <>
      <p className={styles.kicker}>
        {item.year} · {item.kicker}
      </p>
      <h3>{item.title}</h3>
      {item.body.map((p, pi) => (
        <HistoryParagraph key={pi} text={p} />
      ))}
    </>
  );
}

export function HistoryScroll() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [beat, setBeat] = useState(0);
  const [exiting, setExiting] = useState<number | null>(null);
  const [dir, setDir] = useState<1 | -1>(1);
  const [enterReady, setEnterReady] = useState(true);
  const isMobile = useMediaQuery("(max-width: 960px)");
  const reduced = usePrefersReducedMotion();
  const n = historyBeats.length;
  const current = historyBeats[beat] ?? historyBeats[0];

  const applyBeat = useCallback((next: number) => {
    setBeat((prev) => {
      if (next === prev) return prev;
      setDir(next > prev ? 1 : -1);
      setExiting(prev);
      setEnterReady(false);
      return next;
    });
  }, []);

  useEffect(() => {
    if (enterReady) return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEnterReady(true));
    });
    return () => cancelAnimationFrame(id);
  }, [beat, enterReady]);

  useEffect(() => {
    if (exiting === null) return;
    const t = window.setTimeout(() => setExiting(null), 720);
    return () => window.clearTimeout(t);
  }, [exiting, beat]);

  useEffect(() => {
    if (isMobile || reduced) return;
    const track = trackRef.current;
    if (!track) return;

    let raf = 0;
    const read = () => {
      raf = 0;
      applyBeat(beatFromScroll(track, n));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isMobile, reduced, n, applyBeat]);

  const goToBeat = (i: number) => {
    const track = trackRef.current;
    if (!track || isMobile || reduced) {
      applyBeat(i);
      return;
    }
    const { start, span } = trackRange(track);
    const t = (i + 0.32) / n;
    window.scrollTo({ top: start + t * span, behavior: "instant" });
    applyBeat(i);
  };

  const flyClass = (i: number) => {
    if (i === beat && enterReady) return styles.cardOn;
    if (i === beat) return dir === 1 ? styles.fromBelow : styles.fromAbove;
    if (i === exiting) return dir === 1 ? styles.exitUp : styles.exitDown;
    return "";
  };

  const stage = (
    <>
      <header className={styles.head}>
        <h2 className={styles.sectionTitle}>The Story of Mondragón</h2>
        <p className={styles.sectionKicker}>Pioneer. Innovation. Failure.</p>
      </header>
      <div className={styles.stage}>
        <div className={styles.visual}>
          {historyBeats.map((item, i) => (
            <div key={item.id} className={`${styles.year} ${flyClass(i)}`} aria-hidden={i !== beat}>
              {item.year}
            </div>
          ))}
          <div className={styles.sil}>
            <RifleSilhouette />
          </div>
        </div>
        <div className={styles.copy}>
          <div className={styles.cardStage}>
            <div className={styles.sizer} aria-hidden>
              <BeatCopy item={current} />
            </div>
            {historyBeats.map((item, i) => (
              <article
                key={item.id}
                className={`${styles.card} ${flyClass(i)}`}
                aria-hidden={i !== beat}
              >
                <BeatCopy item={item} />
              </article>
            ))}
          </div>
          <div className={styles.rail} role="tablist" aria-label="History chapters">
            {historyBeats.map((item, i) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.tick} ${i === beat ? styles.tickOn : ""}`}
                aria-label={item.title}
                aria-current={i === beat ? "step" : undefined}
                onClick={() => goToBeat(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );

  if (isMobile || reduced) {
    return (
      <section id="history" className={styles.section}>
        <div className={styles.pinInner}>
          <header className={styles.head}>
            <h2 className={styles.sectionTitle}>The Story of Mondragón</h2>
            <p className={styles.sectionKicker}>Pioneer. Innovation. Failure.</p>
          </header>
          <div className={styles.mobileStack}>
            {historyBeats.map((item) => (
              <article key={item.id} className={styles.card}>
                <BeatCopy item={item} />
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="history" className={styles.section}>
      <div className={styles.track} ref={trackRef} style={{ ["--beats" as string]: n }}>
        <div className={styles.pinInner}>{stage}</div>
      </div>
    </section>
  );
}
